/**
 * PlanIndicator — shows a link icon on tasks connected via a plan.
 *
 * - Plan-step tasks (has anchorTaskId): shows chain icon, click → navigate to anchor
 * - Anchor tasks (other tasks point to it): shows chain icon + step count badge
 */

import { memo, useCallback } from "react"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import { cn } from "@/shared/utils/general/cn"
import { useRevealTask } from "@/features/search/useRevealTask"
import LinkIcon from "@/shared/icons/pikaicons/stroke/development/link-chain-horizontal.svg?react"

interface PlanIndicatorProps {
  task: { id?: string; anchorTaskId?: string }
  className?: string
}

/**
 * For a plan-step task: icon + tooltip with anchor task name.
 * For an anchor task: icon + count of linked plan steps.
 */
export const PlanIndicator = memo(function PlanIndicator({
  task,
  className,
}: PlanIndicatorProps) {
  const taskId = task.id as string
  const isStep = Boolean(task.anchorTaskId)

  // If this is a plan step, get the anchor task title
  const [anchorTitle] = useLiveQueryImmediate(
    () =>
      isStep && task.anchorTaskId
        ? db.totodo.get(task.anchorTaskId).then((t) => t?.title ?? "")
        : Promise.resolve(""),
    ["plan-anchor-title", task.anchorTaskId ?? ""],
    "",
  )

  // If this is an anchor task, count how many steps point to it
  const [stepCount] = useLiveQueryImmediate(
    () =>
      !isStep && taskId
        ? db.totodo
            .where("anchorTaskId")
            .equals(taskId)
            .filter((t) => t.isDeleted !== 1)
            .count()
        : Promise.resolve(0),
    ["plan-step-count", taskId, isStep ? "skip" : "count"],
    0,
  )

  const revealTask = useRevealTask()

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isStep && task.anchorTaskId) {
        revealTask(task.anchorTaskId)
      }
    },
    [isStep, task.anchorTaskId, revealTask],
  )

  // Don't render if: not a step AND no steps pointing to this task
  if (!isStep && stepCount === 0) return null

  const tooltip = isStep
    ? anchorTitle || "Linked task"
    : `${stepCount} plan step${stepCount === 1 ? "" : "s"}`

  return (
    <button
      onClick={handleClick}
      title={tooltip}
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 rounded px-0.5 text-foreground/40 transition-colors hover:text-primary",
        isStep && "cursor-pointer",
        !isStep && "cursor-default",
        className,
      )}
    >
      <LinkIcon width={14} height={14} />
      {!isStep && stepCount > 0 && (
        <span className="text-[10px] font-medium leading-none">{stepCount}</span>
      )}
    </button>
  )
})
